import React from "react";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaUserShield,
  FaWarehouse,
  FaClock,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

const ViewUserDrawer = ({user,hubs = [],roles = [],onClose,}) => {
  if (!user) return null;

  const role = roles.find((r) => Number(r.roleId) === Number(user.roleId)) || {};

  const getHubName = (hubId) => {
    const hub = hubs.find((h) => h._id === hubId);
    return (hub?.hub_name || hub?.name || hubId);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30">
      <div className="absolute right-0 top-0 h-screen w-full max-w-md overflow-y-auto bg-white shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-5">
          <h2 className="text-xl font-bold">
            User Details
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <FaTimes />
          </button>
        </div>

        {/* Profile */}
        <div className="flex flex-col items-center border-b p-6">
          {user.userProfileImage ? (
            <img
              src={user.userProfileImage}
              alt="profile"
              className="h-24 w-24 rounded-full border object-cover"
            />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-200">
              <FaUser
                size={40}
                className="text-gray-600"
              />
            </div>
          )}

          <h3 className="mt-4 text-xl font-semibold">
            {user.firstName} {user.lastName}
          </h3>

          <p className="text-sm text-gray-500">
            @{user.username}
          </p>

          <div className="mt-4 flex gap-2">
            {user.is_active ? (
              <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                Active
              </span>
            ) : (
              <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                Inactive
              </span>
            )}

            {user.superAdmin && (
              <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">
                Super Admin
              </span>
            )}
          </div>
        </div>

        {/* Information */}
        <div className="space-y-6 p-6">

          {/* Basic */}
          <div>
            <h4 className="mb-3 font-semibold">
              Basic Information
            </h4>

            <div className="space-y-3">
              <InfoRow
                icon={<FaEnvelope />}
                label="Email"
                value={user.email || "-"}
              />

              <InfoRow
                icon={<FaPhone />}
                label="Mobile"
                value={user.mobileNo || "-"}
              />

              <InfoRow
                icon={<FaUserShield />}
                label="Role"
                value={role.roleName || "-"}
              />

            </div>
          </div>

          {/* Hubs */}
          <div>
            <h4 className="mb-3 font-semibold">
              Assigned Hubs
            </h4>
            <div className="flex flex-wrap gap-2">
              {user.hubIds?.length ? (
                user.hubIds.map((hubId) => (
                  <span
                    key={hubId}
                    className="flex items-center gap-2 rounded-full bg-blue-50 px-3 py-2 text-sm text-blue-700"
                  >
                    <FaWarehouse />
                    {getHubName(hubId)}
                  </span>
                ))
              ) : (
                <span className="text-gray-500">
                  No hubs assigned
                </span>
              )}
            </div>
          </div>

          {/* Account */}
          <div>
            <h4 className="mb-3 font-semibold">
              Account Settings
            </h4>
            <div className="space-y-3">
              <StatusRow
                title="Account Active"
                value={user.is_active}
              />

              <StatusRow
                title="Super Admin"
                value={user.superAdmin}
              />

              <StatusRow
                title="Force Password Change"
                value={user.forcePasswordChange}
              />
            </div>
          </div>

          {/* Audit */}
          <div>

            <h4 className="mb-3 font-semibold">
              Audit Information
            </h4>

            <div className="space-y-3">
              <InfoRow
                icon={<FaClock />}
                label="Last Login"
                value={formatDate(user.lastLogin)}
              />

              <InfoRow
                icon={<FaCalendarAlt />}
                label="Created At"
                value={formatDate(user.createdAt)}
              />

              <InfoRow
                icon={<FaCalendarAlt />}
                label="Updated At"
                value={formatDate(user.updatedAt)}
              />

              <InfoRow
                label="Created By"
                value={
                  user.createdBy?.username || "-"
                }
              />

              <InfoRow
                label="Updated By"
                value={
                  user.updatedBy?.username || "-"
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({icon,label,value,}) => (
  <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
    <div className="flex items-center gap-2">
      {icon}
      <span className="font-medium">
        {label}
      </span>
    </div>
    <span className="text-gray-600 text-right">
      {value}
    </span>
  </div>
);

const StatusRow = ({title,value,}) => (
  <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
    <span className="font-medium">
      {title}
    </span>
    {value ? (
      <span className="flex items-center gap-2 text-green-600">
        <FaCheckCircle />
        Yes
      </span>
    ) : (
      <span className="flex items-center gap-2 text-red-600">
        <FaTimesCircle />
        No
      </span>
    )}
  </div>
);

export default ViewUserDrawer;